import React, { useState } from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import FunwooAPI, { Agent } from '@/swagger';
import { AxiosError } from 'axios';
import { GetStaticProps, NextPage } from 'next';
import OpenGraph from '@/components/OpenGraph';
import Fallback from '@/components/Fallback';
import { useRouter } from 'next/router';
import FullPage from '@/src/components/UI/FullPage';
import clsx from 'clsx';
import Image from 'next/image';
import { Button } from '@chakra-ui/react';
import { ShareSocial } from '@/components/feature/ShareSocial';
import { isBrowser } from '@chakra-ui/utils';
import { LinePopup } from '@/src/components/UI/HouseDetail/ModalPopup';
import { isServer } from '@/src/libs/is-next-server';
import ReactTooltip from 'react-tooltip';
import CallMeBackForm from '@/src/components/UI/Forms/CallMeBackForm';
import Modal from '@/src/components/Modal/Modal';

const NANCY_ID = 'cda7bba6-9795-411f-8f2a-393043d1801c';

interface Props {
  data: Agent;
}

const AgentDetail: NextPage<Props> = ({ data }) => {
  const phone = process.env.NEXT_PUBLIC_FUNWOO_TAIWAN_CELLPHONE;
  const bookingUrl = `${process.env.FUNWOO_AGENT_PLATFORM_URL}/booking/${data?.sid}`;
  const router = useRouter();

  const [showLinePopup, setShowLinePopup] = useState(false);
  const [showCallMeBackForm, setShowCallMeBackForm] = useState(false);

  if (router.isFallback) {
    return <Fallback />;
  }

  return (
    <React.Fragment>
      <LinePopup
        lineQR={'https://line.me/ti/p/@funwoo'}
        setShowLinePopup={setShowLinePopup}
        showLinePopup={showLinePopup}
      />
      <Modal show={showCallMeBackForm}>
        <CallMeBackForm agent={data} closeBtnPress={() => setShowCallMeBackForm(false)} needAddress={false} />
      </Modal>
      {!isServer() ? <ReactTooltip backgroundColor='black' place={'bottom'} /> : null}
      <OpenGraph
        title={`FUNWOO 房產顧問 ${data.name} ${data.english_name}`}
        description={`有任何買賣屋諮詢？立即撥打 ${phone} 聯絡 FUNWOO 房產顧問${data.name}`}
        type='funwoo.agent'
        url={`mailto:${data.email}?subject=聯絡 FUNWOO 房產顧問${data.name}`}
        //@ts-ignore
        imageUrl={data?.pictures?.[0] ?? ''}
        keywords={[
          '房產顧問',
          '房仲',
          'FUNWOO 房產顧問',
          data.chinese_name,
          data.english_name,
          data.email,
          data.contact_phone,
        ]}
      />
      <FullPage hasPadding={false} hasHeaderPlaceHolder>
        <div className={clsx('px-5', 'sm:py-5')}>
          <div className={clsx('relative', 'sm:py-5')}>
            <div className={clsx('md:flex md:items-center', 'md:space-x-10')}>
              <div className={clsx('space-y-5', 'md:flex-1')}>
                <div
                  className={clsx(
                    'space-y-5 pb-5',
                    'sm:flex sm:items-end md:items-center',
                    'sm:space-y-0 sm:space-x-5 md:space-x-10'
                  )}
                >
                  <div className={clsx('sm:w-1/3 md:w-[356px]')}>
                    <Image
                      priority
                      width={382}
                      height={382}
                      layout={'responsive'}
                      objectFit={'cover'}
                      className={'bg-gray300 object-top'}
                      //@ts-ignore
                      src={data?.pictures?.[0] ?? ''}
                    />
                  </div>
                  <div className={clsx('space-y-5')}>
                    <div className={clsx('space-y-2')}>
                      <div className={clsx('space-y-2')}>
                        <span className={clsx('border border-gray500', 'py-0.5 px-2', 'text-gray500 text-xs')}>
                          {data.id === NANCY_ID ? '執行長暨創辦人' : '房產顧問要'}
                        </span>
                        <p>
                          {data.chinese_name} {data.english_name}
                        </p>
                      </div>
                      <p>
                        {phone} {data.contact_phone_ext ? `#${data.contact_phone_ext}` : ''}
                      </p>
                      <p className={clsx('py-1')}>{data.email}</p>
                    </div>
                    <Button as={'a'} href={bookingUrl} target={'_blank'} rel={'noopener noreferrer'} w={{ base: 180 }}>
                      預約時間
                    </Button>
                  </div>
                </div>
              </div>
              <div className={clsx('my-4 md:my-0', 'w-full h-px', 'md:w-px md:h-[17.25rem]', 'bg-gray900')} />
              <div className={clsx('pt-4 pb-8', 'md:flex-1')}>
                <div className={clsx('space-y-2')}>
                  <p className={clsx('text-lg')}>關於</p>
                  <p className={clsx('whitespace-pre-wrap')}>{data.bio}</p>
                </div>
              </div>
            </div>
            <div className={clsx('mt-4', 'space-x-2', 'flex', 'sm:absolute sm:top-0 sm:right-0')}>
              <IconBtn
                toolTipString={'掃描 QR 碼'}
                onPress={() => setShowLinePopup(true)}
                iconURL={'/icons-line-white.png'}
              />
              <IconBtn
                toolTipString={'回電給我'}
                onPress={() => setShowCallMeBackForm(true)}
                iconURL={'/icons-contact.png'}
              />
              <ShareSocial
                buttonClassName={clsx('w-12 h-12', 'flex items-center justify-center', 'hover:bg-gray100')}
                className={clsx('w-6 h-6')}
                url={isBrowser ? window.location.href : ''}
                title={isBrowser ? document.title : ''}
              />
            </div>
          </div>
        </div>
      </FullPage>
    </React.Fragment>
  );
};

// noinspection JSUnusedGlobalSymbols
export async function getStaticPaths({ locales }: { locales: Array<string> }) {
  const { data: agentSids } = await FunwooAPI.agentApi.findAllSids();
  const paths = agentSids
    .map((sid) =>
      locales.map((locale) => ({
        locale,
        params: { sid },
      }))
    )
    .flat();
  return {
    paths,
    fallback: true,
  };
}

export const getStaticProps: GetStaticProps<Props, { sid: string }> = async ({
  locale = '',
  params: { sid } = { sid: '' },
}) => {
  try {
    const { data } = await FunwooAPI.agentApi.findOne(sid.toLocaleLowerCase()).catch((error: AxiosError) => {
      if (error.response?.status === 409) return { data: null };
      else throw error;
    });

    if (!data || !data.active) {
      return {
        notFound: true,
        revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
      };
    }
    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
        data,
      },
      revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
    };
  } catch (e) {
    return {
      notFound: true,
      revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
    };
  }
};
export default AgentDetail;

interface IconBtnProps {
  onPress: () => void;
  iconURL: string;
  toolTipString: string;
}

const IconBtn: React.FC<IconBtnProps> = ({ onPress, iconURL, toolTipString }) => {
  return (
    <div
      data-tip={toolTipString}
      onClick={onPress}
      className={clsx('w-12 h-12', 'hover:bg-gray100', 'items-center justify-center flex')}
    >
      <Image width={24} height={24} src={iconURL} />
    </div>
  );
};
